import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { InlineWidget } from 'react-calendly';
import Carousel from 'react-material-ui-carousel';
import { useQuery } from '@tanstack/react-query';
import { getBookings } from '../../../services';
import { ReactComponent as ConfirmSVG } from './assets/confirm.svg';
import { AuthContext } from '../../shared/app/AuthProvider';
import { addOffering, selectCommon } from '../../../store/common';
import OfferingDetails from './offering-details';
import {
  fetchBookingsHistory,
  selectBookingsHistory,
  selectFilters,
} from '../../../store';
import { useStyles } from './styles';
import useSearchParams from '../../shared/hook/useSearchParams';
import useGetEvents from '../../shared/hook/use-get-events';

const OfferingSiderContent = ({ scrolled, onClose, page }: any) => {
  const { t } = useTranslation();
  const searchparams = useSearchParams();
  const { authUser } = React.useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { createBookingLoading } = useSelector(selectBookingsHistory);
  const { offering } = useSelector(selectCommon);
  const filters = useSelector(selectFilters);

  const { recommendationId, communitySessions } = useGetEvents();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const { refetch: refetchMyBookings } = useQuery(['my-bookings'], getBookings);

  const isRecommendedEvent = Boolean(
    communitySessions.find(({ id }) => id === offering?.id)?.isRecommended,
  );

  useEffect(() => {
    if (createBookingLoading) {
      setShowLoader(true);
    }
  }, [createBookingLoading]);

  useEffect(() => {
    if (!createBookingLoading && showLoader) {
      setShowConfirmation(true);

      setTimeout(() => {
        setShowLoader(false);
        setShowConfirmation(false);

        setTimeout(() => {
          dispatch(addOffering({}));
          history.push('/bookings');
          refetchMyBookings();
          dispatch(fetchBookingsHistory());
        });
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createBookingLoading, showLoader]);

  const utmCampaign = searchparams.get('utm_campaign') ?? 'WEBAPP';

  return (
    <>
      {offering && (
        <Carousel
          navButtonsAlwaysInvisible
          autoPlay={false}
          indicators={false}
          index={page}
        >
          <OfferingDetails
            offering={offering}
            onClose={onClose}
            scrolled={scrolled}
          />
          <Box style={{ position: 'relative' }}>
            <InlineWidget
              url={`${offering?.bookingUrl}/${offering?.sessions[0].startsAt}`}
              styles={{
                width: '100%',
                height: '1200px',
              }}
              prefill={{
                email: authUser.attributes.email,
                name: authUser.attributes.given_name,
              }}
              pageSettings={{
                hideGdprBanner: true,
                hideLandingPageDetails: true,
                backgroundColor: 'ffffff',
              }}
              utm={{
                ...(isRecommendedEvent && { utmSource: recommendationId }),
                utmTerm: JSON.stringify(filters),
                utmCampaign,
              }}
            />
            <Box className={classes.emptyBox} />
          </Box>
        </Carousel>
      )}
      {showLoader && (
        <Box className={classes.loadingContainer}>
          {showConfirmation ? (
            <ConfirmSVG />
          ) : (
            <Box style={{ position: 'relative' }}>
              <CircularProgress
                classes={{ circle: classes.circleBackground }}
                size={70}
                thickness={5}
                variant="determinate"
                value={100}
              />
              <CircularProgress
                className={classes.progress}
                classes={{ circle: classes.circle }}
                size={70}
                thickness={5}
              />
            </Box>
          )}
          <Typography className={classes.loadingInfo} variant="body1">
            {t('v2.global.loadingIndicator_label_processing')}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default OfferingSiderContent;
